import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { ChatDots, PaperPlaneRight, ArrowLeft, Circle } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getH = () => ({});

export default function Messages() {
    const { user } = useAuth();
    const [conversations, setConversations] = useState([]);
    const [activeThread, setActiveThread] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMsg, setNewMsg] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => { fetchConversations(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);

    const fetchConversations = async () => {
        try {
            const res = await fetch(`${API}/messages/conversations`, { credentials: 'include', headers: getH() });
            if (res.ok) setConversations(await res.json());
        } catch (e) { console.error('Messages fetch error:', e); } finally { setLoading(false); }
    };

    const openThread = async (userId) => {
        setActiveThread(userId);
        const res = await fetch(`${API}/messages/thread/${userId}`, { credentials: 'include', headers: getH() });
        if (res.ok) { setMessages(await res.json()); fetchConversations(); }
    };

    const sendMessage = async () => {
        if (!newMsg.trim() || !activeThread) return;
        setSending(true);
        try {
            const res = await fetch(`${API}/messages/send`, {
                method: 'POST', headers: { ...getH(), 'Content-Type': 'application/json' },
                credentials: 'include', body: JSON.stringify({ to_user_id: activeThread, text: newMsg })
            });
            if (res.ok) { setNewMsg(''); openThread(activeThread); }
            else { const e = await res.json(); toast.error(e.detail || 'Failed'); }
        } catch (e) { toast.error('Send failed'); }
        finally { setSending(false); }
    };

    const fmtTime = (t) => { try { return new Date(t).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }); } catch (e) { return ''; } };
    const activeUser = conversations.find(c => c.user_id === activeThread);

    return (
        <Layout>
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
                {!activeThread ? (
                    <>
                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                            <span className="label-text text-cyber-purple text-glow-purple">MESSAGES</span>
                            <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display">Inbox</h1>
                        </motion.div>
                        {loading ? (
                            <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
                        ) : conversations.length === 0 ? (
                            <Card className="glass-card text-center py-16">
                                <ChatDots size={48} className="text-muted-foreground mx-auto mb-3" />
                                <p className="text-muted-foreground font-display">No messages yet</p>
                                <p className="text-sm text-muted-foreground mt-1">Follow riders and start chatting</p>
                            </Card>
                        ) : (
                            <div className="space-y-2">
                                {conversations.map(c => (
                                    <Card key={c.user_id} className="glass-card cursor-pointer hover:border-cyber-purple/20" onClick={() => openThread(c.user_id)}>
                                        <CardContent className="p-3 flex items-center gap-3">
                                            <Avatar className="w-10 h-10">
                                                <AvatarImage src={c.picture} />
                                                <AvatarFallback className="bg-cyber-purple/20 text-cyber-purple text-xs">{c.name?.charAt(0) || '?'}</AvatarFallback>
                                            </Avatar>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-bold truncate">{c.name || 'Rider'}</p>
                                                <p className="text-xs text-muted-foreground truncate">{c.last_message}</p>
                                            </div>
                                            {c.unread > 0 && (
                                                <Badge className="bg-primary text-black text-xs font-mono">{c.unread}</Badge>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </>
                ) : (
                    /* Thread View */
                    <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}>
                        <div className="flex items-center gap-3 mb-4">
                            <Button variant="ghost" size="sm" onClick={() => { setActiveThread(null); setMessages([]); }}>
                                <ArrowLeft size={18} />
                            </Button>
                            <Avatar className="w-8 h-8">
                                <AvatarImage src={activeUser?.picture} />
                                <AvatarFallback className="bg-cyber-purple/20 text-xs">{activeUser?.name?.charAt(0) || '?'}</AvatarFallback>
                            </Avatar>
                            <p className="font-bold text-sm font-display">{activeUser?.name || 'Rider'}</p>
                        </div>

                        <div ref={scrollRef} className="h-[55vh] overflow-y-auto space-y-2 mb-4 px-1" data-testid="message-thread">
                            {messages.map(m => (
                                <div key={m.message_id} className={`flex ${m.from_user_id === user?.user_id ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`max-w-[75%] px-4 py-2 rounded-2xl text-sm ${m.from_user_id === user?.user_id ? 'bg-primary/20 text-foreground rounded-br-md' : 'bg-white/5 text-foreground rounded-bl-md'}`}>
                                        <p>{m.text}</p>
                                        <p className="text-[9px] text-muted-foreground mt-1">{fmtTime(m.created_at)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="flex gap-2">
                            <Input value={newMsg} onChange={e => setNewMsg(e.target.value)} placeholder="Type a message..." className="flex-1"
                                onKeyDown={e => e.key === 'Enter' && sendMessage()} data-testid="message-input" />
                            <Button onClick={sendMessage} disabled={sending || !newMsg.trim()} className="bg-primary text-black" data-testid="send-message-btn">
                                <PaperPlaneRight size={18} />
                            </Button>
                        </div>
                    </motion.div>
                )}
            </div>
        </Layout>
    );
}
